import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DatePicker } from "rsuite";

import { useInput } from "hooks/useInput";
import { createFilterProps, getDefaultInputValue } from "utility/form";
import { renderConditionalProp } from "utility";

import { Dialog } from "components/common/dialog/Dialog";

import { useRootDispatch, useRootSelector } from "store/hooks";
import { FilterProps } from "store/common";

import {
  clearFilter,
  setFilters,
} from "store/paymentActivity/paymentActivity-slice";
import {
  activeAccountTypeConfig,
  selectedAccountInfo,
} from "store/account/account-selector";
import { useRequestDispatch } from "store/requestHooks";
import {
  FetchPayments,
  fetchPayments,
} from "store/paymentActivity/paymentActivity-action";

import classes from "./Filters.module.scss";

export interface FiltersProps {
  setOpenDialog: (close: boolean) => void;
  filters?: FilterProps | null;
}

export const Filters: React.FC<FiltersProps> = ({ setOpenDialog, filters }) => {
  const { t } = useTranslation(["common", "payment", "payment_final"]);

  const config = useRootSelector(activeAccountTypeConfig);
  const accountInfo = useRootSelector(selectedAccountInfo);

  const [startDate, handleStartChange] = useState<Date | null>(
    filters?.minDate ? new Date(filters.minDate) : null,
  );
  const [endDate, handleEndChange] = useState<Date | null>(
    filters?.maxDate ? new Date(filters.maxDate) : null,
  );
  const status = useInput(getDefaultInputValue(filters?.status, "0"));
  const paymentMethodType = useInput(
    getDefaultInputValue(filters?.paymentMethodType, "0"),
  );
  const confirmantionNo = useInput(
    getDefaultInputValue(filters?.confirmationNumber),
  );

  const dispatch = useRootDispatch();
  const requestDispatch = useRequestDispatch();

  const resetButtonHandler = () => {
    const paymentFilter: FilterProps = {
      accountNumber: accountInfo?.accountNumber,
    };
    paymentMethodType.setValue("0");
    status.setValue("0");
    confirmantionNo.setValue("");
    handleStartChange(null);
    handleEndChange(null);
    dispatch(clearFilter());
    dispatch(setFilters(paymentFilter));
    requestDispatch<FetchPayments, FilterProps>(fetchPayments, paymentFilter);
  };

  const applyFilterHandler = () => {
    const filters: FilterProps = {
      accountNumber: accountInfo?.accountNumber,
      minDate: startDate && startDate.toDateString(),
      maxDate: endDate && endDate.toDateString(),
      confirmationNumber: confirmantionNo.value,
      status: status.value === "0" ? null : status.value,
      paymentMethodType:
        paymentMethodType.value === "0" ? null : paymentMethodType.value,
    };

    const filterProps = createFilterProps(filters);

    if (filterProps) {
      dispatch(setFilters(filters));
      requestDispatch<FetchPayments, FilterProps>(fetchPayments, filterProps);
    }
    setOpenDialog(false);
  };

  const datePickerOpenHandler = () => {
    const datePicker = document.getElementsByClassName("rs-picker-menu");
    datePicker[0] && datePicker[0].removeAttribute("inert");
  };

  useEffect(() => {
    const startDateWrapper = document.getElementsByClassName(
      "account_summ_start_data",
    );
    if (startDateWrapper) {
      const labelPlacehlder = startDateWrapper[0].getElementsByTagName("span");
      labelPlacehlder && labelPlacehlder[0].removeAttribute("aria-placeholder");
      startDateWrapper[0]
        .getElementsByTagName("input")[0]
        .setAttribute("aria-placeholder", labelPlacehlder[0].innerText);
    }
  }, [startDate]);

  useEffect(() => {
    const endDateWrapper = document.getElementsByClassName(
      "account_summ_end_data",
    );
    if (endDateWrapper) {
      const labelPlacehlder = endDateWrapper[0].getElementsByTagName("span");
      labelPlacehlder && labelPlacehlder[0].removeAttribute("aria-placeholder");
      endDateWrapper[0]
        .getElementsByTagName("input")[0]
        .setAttribute("aria-placeholder", labelPlacehlder[0].innerText);
    }
  }, [endDate]);

  //className="account_summ_start_data"

  return (
    <Dialog hideIndication hideActions closeIcon dialogClosed={setOpenDialog}>
      <section className={classes.Wrapper}>
        <p>{t("payment_final:date_range")}</p>
        <div className={["rsuite-custom", classes.FieldWrapper].join(" ")}>
          <DatePicker
            format="MM-dd-yyyy"
            isoWeek
            onOpen={datePickerOpenHandler}
            block
            oneTap
            placeholder={t("payment_final:start_date")}
            value={startDate}
            onChange={handleStartChange}
            size="lg"
            placement="auto"
            className="account_summ_start_data"
          />
        </div>
        <div className={["rsuite-custom", classes.FieldWrapper].join(" ")}>
          <DatePicker
            format="MM-dd-yyyy"
            isoWeek
            onOpen={datePickerOpenHandler}
            block
            oneTap
            placeholder={t("payment_final:end_date")}
            value={endDate}
            onChange={handleEndChange}
            size="lg"
            placement="auto"
            className="account_summ_end_data"
          />
        </div>
        <ft-divider></ft-divider>
        <p>{t("payment_final:confirmation_number_search")}</p>
        <div className={classes.FieldWrapper}>
          <ft-textfield
            outlined
            label={t("payment_final:enter_confirmation_number")}
            value={confirmantionNo.value}
            ref={confirmantionNo.ref}
            onInput={confirmantionNo.valueChangeHandler}
            onBlur={confirmantionNo.inputBlurHandler}
          ></ft-textfield>
        </div>
        <ft-divider></ft-divider>
        {/* <p>Status</p> */}
        <div className={classes.FieldWrapper}>
          <ft-select
            outlined
            onClick={status.valueChangeHandler}
            value={status.value}
            onBlur={status.inputBlurHandler}
            ref={status.ref}
            label={t("payment:status")}
          >
            <ft-list-item
              value="0"
              {...renderConditionalProp(status.value === "0", "selected")}
            >
              {t("payment_final:all_status")}
            </ft-list-item>
            <ft-list-item
              value="2"
              {...renderConditionalProp(status.value === "2", "selected")}
            >
              {t("payment_final:paid")}
            </ft-list-item>
            <ft-list-item
              value="3"
              {...renderConditionalProp(status.value === "3", "selected")}
            >
              {t("payment_final:pending")}
            </ft-list-item>
            <ft-list-item
              value="1"
              {...renderConditionalProp(status.value === "1", "selected")}
            >
              {t("payment_final:canceled")}
            </ft-list-item>
            <ft-list-item
              value="9"
              {...renderConditionalProp(status.value === "9", "selected")}
            >
              {t("payment_final:refunded")}
            </ft-list-item>
            <ft-list-item
              value="4"
              {...renderConditionalProp(status.value === "4", "selected")}
            >
              {t("payment_final:returned")}
            </ft-list-item>
          </ft-select>
        </div>
        <ft-divider></ft-divider>
        {/* <p>Payment Method</p> */}
        <div className={classes.FieldWrapper}>
          <ft-select
            outlined
            label={t("payment:paymentMethodName")}
            onClick={paymentMethodType.valueChangeHandler}
            value={paymentMethodType.value}
            ref={paymentMethodType.ref}
            onBlur={paymentMethodType.inputBlurHandler}
          >
            <ft-list-item
              {...renderConditionalProp(
                paymentMethodType.value === "0",
                "selected",
              )}
              value="0"
            >
              {t("payment_final:all_payment_methods")}
            </ft-list-item>
            {config?.paymentMethods?.card?.enabled && (
              <ft-list-item
                {...renderConditionalProp(
                  paymentMethodType.value === "2",
                  "selected",
                )}
                value="2"
              >
                {t("common:card_label")}
              </ft-list-item>
            )}
            {config?.paymentMethods?.ach?.enabled && (
              <ft-list-item
                {...renderConditionalProp(
                  paymentMethodType.value === "1",
                  "selected",
                )}
                value="1"
              >
                {t("common:ach_label")}
              </ft-list-item>
            )}
            {config?.paymentMethods?.eWallet?.enabled && (
              <ft-list-item
                {...renderConditionalProp(
                  paymentMethodType.value === "3",
                  "selected",
                )}
                value="3"
              >
                {t("common:eWallet_label")}
              </ft-list-item>
            )}
          </ft-select>
        </div>
        <div className={classes.ButtonWrapper}>
          <ft-button
            onClick={resetButtonHandler}
            class={[classes.ResetFilter, "outline-button"].join(" ")}
            label={t("payment_final:reset_filter")}
            outlined
          ></ft-button>
          <ft-button
            class={classes.ApplyFilter}
            label={t("payment_final:apply_filter")}
            unelevated
            onClick={applyFilterHandler}
          ></ft-button>
        </div>
      </section>
    </Dialog>
  );
};
